import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { API, setAuthToken } from '@/lib/api';
import { toast } from 'sonner';
import { User, LogOut, Crown } from 'lucide-react';
import Navigation from '@/components/Navigation';
import { useNavigate } from 'react-router-dom';

function Profile() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const response = await API.get('/auth/me');
      setUser(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load profile');
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await API.post('/auth/logout');
      setAuthToken(null);
      document.cookie = 'session_token=; path=/; max-age=0';
      toast.success('Logged out');
      navigate('/');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  const handleUpgrade = async () => {
    try {
      const response = await API.post('/payments/checkout', {
        plan: 'monthly',
        origin_url: window.location.origin
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to start checkout');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navigation />
      
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="flex flex-col items-center mb-12">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center mb-4">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-24 h-24 rounded-full object-cover" />
            ) : (
              <User className="w-12 h-12 text-white" />
            )}
          </div>
          <h1 className="font-['Outfit'] text-4xl font-bold mb-2 text-center" data-testid="user-name">{user?.name}</h1>
          <p className="text-gray-400 text-center" data-testid="user-email">{user?.email}</p>
        </div>

        <div className="ninja-card mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-['Outfit'] text-2xl font-bold">Subscription</h2>
            {user?.subscription_status === 'premium' ? (
              <div className="flex items-center gap-2 px-4 py-2 bg-yellow-500/20 border border-yellow-500/40 rounded-full">
                <Crown className="text-yellow-500 w-5 h-5" />
                <span className="text-yellow-500 font-bold" data-testid="subscription-status">Premium</span>
              </div>
            ) : (
              <div className="px-4 py-2 bg-white/10 border border-white/20 rounded-full">
                <span className="text-gray-400 font-semibold" data-testid="subscription-status">Free</span>
              </div>
            )}
          </div>

          {user?.subscription_status === 'free' && (
            <div>
              <p className="text-gray-400 mb-4">Upgrade to Premium for unlimited AI roleplays, advanced analytics, and more.</p>
              <Button 
                onClick={handleUpgrade}
                className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold"
                data-testid="upgrade-to-premium-button"
              >
                <Crown className="w-5 h-5 mr-2" /> Upgrade to Premium
              </Button>
            </div>
          )}

          {user?.subscription_status === 'premium' && (
            <p className="text-gray-400">You have unlimited access to all premium features.</p>
          )}
        </div>

        <div className="ninja-card mb-6">
          <h2 className="font-['Outfit'] text-2xl font-bold mb-4">Account</h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2">
              <span className="text-gray-400">Member since</span>
              <span className="font-semibold">{new Date(user?.created_at).toLocaleDateString()}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-gray-400">Roleplays this month</span>
              <span className="font-semibold">{user?.roleplay_count_this_month || 0}</span>
            </div>
          </div>
        </div>

        <Button 
          onClick={handleLogout}
          variant="outline"
          className="w-full border-red-500/50 hover:bg-red-500/10 text-red-400"
          data-testid="logout-button"
        >
          <LogOut className="w-5 h-5 mr-2" /> Logout
        </Button>
      </div>
    </div>
  );
}

export default Profile;